import React from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { Navigate } from "react-router-dom";
import { Spin } from "antd";

const AUTH_BYPASS = import.meta.env["VITE_AUTH_BYPASS"] === "true";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Renders children only when the user is authenticated via MSAL.
 * Unauthenticated users are redirected to /login.
 * Waits for MSAL to finish handling redirects before deciding.
 * Set VITE_AUTH_BYPASS=true in .env to skip auth during local development.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps): React.JSX.Element {
  const { inProgress } = useMsal();

  if (AUTH_BYPASS) {
    return <>{children}</>;
  }

  // Wait for MSAL to finish processing the redirect response
  if (
    inProgress === InteractionStatus.HandleRedirect ||
    inProgress === InteractionStatus.AcquireToken ||
    inProgress === InteractionStatus.Startup
  ) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <Spin size="large" tip="Authentification en cours..." />
      </div>
    );
  }

  return (
    <>
      <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Navigate to="/login" replace />
      </UnauthenticatedTemplate>
    </>
  );
}
